import type { Security, SecurityOverride } from "~types/types";

const CRYPTO_TICKER_LIST = [
    "XBT:CUR",
    "XET:CUR",
    "XLC:CUR",
    "XTH:CUR",
    "XEO:CUR",
    "XDH:CUR",
    "XBN:CUR",
];

const TICKERBAR_DROPDOWN: { [setOption: string]: string[] } = {
    top: [
        "SPX:IND",
        "CCMP:IND",
        "B500:IND",
        "GT10:GOV",
        "CL1:COM",
        "UKX:IND",
        "GC1:COM",
        "EURUSD:CUR",
        "GBPUSD:CUR",
    ],
    indices: [
        "SPX:IND",
        "B500:IND",
        "INDU:IND",
        "RTY:IND",
        "CCMP:IND",
        "NKY:IND",
        "HSI:IND",
        "UKX:IND",
        "DAX:IND",
    ],
    rates: [
        "GT5:GOV",
        "GT10:GOV",
        "GTJPY10Y:GOV",
        "GTGBP10Y:GOV",
        "GTFRF10Y:GOV",
        "GTDEM10Y:GOV",
        "GTCAD10Y:GOV",
        "GTHKD10Y:GOV",
    ],
};

const PHOENIX_TICKER_BARS: {
    [pageId: string]: { [setOption: string]: string[] };
} = {
    default: TICKERBAR_DROPDOWN,
    "phoenix-uk": {
        ...TICKERBAR_DROPDOWN,
        top: [
            "UKX:IND",
            "SPX:IND",
            "B500:IND",
            "GBPUSD:CUR",
            "EURGBP:CUR",
            "CL1:COM",
            "CCMP:IND",
            "DAX:IND",
            "FTSEMIB:IND",
        ],
    },
    "phx-ai": {
        ...TICKERBAR_DROPDOWN,
        top: [
            "MSFT:US",
            "GOOGL:US",
            "NVDA:US",
            "BIDU:US",
            "SNTMF:US",
            "AMZN:US",
            "PLTR:US",
            "TSLA:US",
            "IBM:US",
            "META:US",
            "MU:US",
            "CRM:US",
            "AAPL:US",
        ],
    },
    "phx-crypto": {
        ...TICKERBAR_DROPDOWN,
        top: [
            "XBT:CUR",
            "XET:CUR",
            "XLC:CUR",
            "XTH:CUR",
            "XEO:CUR",
            "XDH:CUR",
            "XBN:CUR",
        ],
    },
    crypto: {
        ...TICKERBAR_DROPDOWN,
        top: [
            "XBT:CUR",
            "XET:CUR",
            "XLC:CUR",
            "XTH:CUR",
            "XEO:CUR",
            "XDH:CUR",
            "XBN:CUR",
        ],
    },
};

const TICKER_BARS: { [pageId: string]: string[] } = {
    default: [
        "SPX:IND",
        "CCMP:IND",
        "CL1:COM",
        "GT10:GOV",
        "EURUSD:CUR",
        "INDU:IND",
        "RTY:IND",
        "GC1:COM",
        "XAG:CUR",
        "NKY:IND",
        "HSI:IND",
        "UKX:IND",
        "DAX:IND",
        "GBPUSD:CUR",
        "USDJPY:CUR",
        "XBT:CUR",
        "XET:CUR",
    ],
    "premium-uk": [
        "UKX:IND",
        "SPX:IND",
        "GBPUSD:CUR",
        "EURGBP:CUR",
        "CO1:COM",
        "CCMP:IND",
        "DAX:IND",
        "FTSEMIB:IND",
        "NKY:IND",
        "HSI:IND",
        "GC1:COM",
        "XAG:CUR",
        "XBT:CUR",
        "XET:CUR",
    ],
    "cryptocurrencies-v2": CRYPTO_TICKER_LIST,
    cryptocurrencies: CRYPTO_TICKER_LIST,
    "technology-ai": [
        "MSFT:US",
        "GOOGL:US",
        "NVDA:US",
        "BIDU:US",
        "SNTMF:US",
        "AMZN:US",
        "PLTR:US",
        "TSLA:US",
        "IBM:US",
        "META:US",
        "MU:US",
        "CRM:US",
        "AAPL:US",
    ],
    phoenix: [...new Set(Object.values(PHOENIX_TICKER_BARS.default).flat())],
    "phoenix-uk": [
        ...new Set(Object.values(PHOENIX_TICKER_BARS["phoenix-uk"]).flat()),
    ],
};

const OVERRIDES: Record<string, SecurityOverride> = {
    "SPX:IND": {
        name: "S&P 500",
    },
    "CCMP:IND": {
        name: "Nasdaq",
    },
    "B500:IND": {
        name: "B500",
    },
    "CL1:COM": {
        name: "Crude Oil",
    },
    "GT5:GOV": {
        name: "US 5 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds/government-bonds/us",
        useLastYield: true,
    },
    "GT10:GOV": {
        name: "US 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds/government-bonds/us",
        useLastYield: true,
    },
    "GTJPY10Y:GOV": {
        name: "JPN 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds/government-bonds/japan",
        useLastYield: true,
    },
    "GTGBP10Y:GOV": {
        name: "UK 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds/government-bonds/uk",
        useLastYield: true,
    },
    "GTFRF10Y:GOV": {
        name: "Fra 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds",
        useLastYield: true,
    },
    "GTDEM10Y:GOV": {
        name: "Bund 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds/government-bonds/germany",
        useLastYield: true,
    },
    "GTCAD10Y:GOV": {
        name: "CAD 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds",
        useLastYield: true,
    },
    "GTHKD10Y:GOV": {
        name: "CNY 10 Yr",
        url: "https://www.bloomberg.com/markets/rates-bonds",
        useLastYield: true,
    },
    "EURUSD:CUR": {
        name: "Euro",
    },
    "INDU:IND": {
        name: "Dow Jones",
    },
    "RTY:IND": {
        name: "Russell 2000",
    },
    "GC1:COM": {
        name: "Gold",
    },
    "XAG:CUR": {
        name: "Silver",
    },
    "NKY:IND": {
        name: "Nikkei 225",
    },
    "HSI:IND": {
        name: "Hang Seng",
    },
    "UKX:IND": {
        name: "FTSE 100",
    },
    "DAX:IND": {
        name: "DAX",
    },
    "GBPUSD:CUR": {
        name: "Pound/Dollar",
    },
    "USDJPY:CUR": {
        name: "Dollar/Yen",
    },
    "USDGBP:CUR": {
        name: "Dollar/Pound",
    },
    "EURGBP:CUR": {
        name: "Euro/Pound",
    },
    "CO1:COM": {
        name: "Brent Crude Oil",
    },
    "FTSEMIB:IND": {
        name: "FTSE MIB",
    },
    "MSFT:US": {
        name: "Microsoft",
    },
    "GOOGL:US": {
        name: "Alphabet",
    },
    "NVDA:US": {
        name: "Nvidia",
    },
    "BIDU:US": {
        name: "Baidu",
    },
    "SNTMF:US": {
        name: "SenseTime",
    },
    "AMZN:US": {
        name: "Amazon",
    },
    "PLTR:US": {
        name: "Palantir",
    },
    "TSLA:US": {
        name: "Tesla",
    },
    "IBM:US": {
        name: "IBM",
    },
    "META:US": {
        name: "Meta",
    },
    "MU:US": {
        name: "Micron",
    },
    "CRM:US": {
        name: "Salesforce",
    },
    "AAPL:US": {
        name: "Apple",
    },
};

function formatTickerBarSecurity(security: Security) {
    const {
        percentChange1Day,
        price,
        shortName,
        lastYield,
        realTimeTickerDescription,
    } = security;

    const ticker = security.id;
    const name = OVERRIDES[ticker]?.name || shortName;

    if (!name) {
        console.error(`No name found for security with id ${security.id}`);
        return undefined;
    }

    const url = OVERRIDES[ticker]?.url || `/quote/${ticker}`;
    const useLastYield = OVERRIDES[ticker]?.useLastYield || false;
    const cleanPercentage1Day = percentChange1Day || 0;
    const intradayPrices = security.intradayPrices?.series || [];

    const tickerBarData = {
        ticker,
        name,
        percentChange1Day: cleanPercentage1Day,
        price: useLastYield ? lastYield : price,
        url,
        intradayPrices,
        realTimeTickerDescription,
    };

    return tickerBarData;
}

export { TICKER_BARS, PHOENIX_TICKER_BARS, OVERRIDES, formatTickerBarSecurity };
